import { useSuspenseQuery } from "@tanstack/react-query";
import { type PropsWithChildren, useMemo } from "react";
import { assert } from "utils";

import {
  SpaceRepositoryProviders,
  type SpaceRepositories,
} from "src/components/SpaceRepositoryProviders";
import { organizationsQueryOptions } from "src/queries/organizations";
import { createOrganizationAthletesRepository } from "src/repositories/athletes/createOrganizationAthletesRepository";
import { createOrganizationExercisesRepository } from "src/repositories/exercises/createOrganizationExercisesRepository";
import { createOrganizationLabelsRepository } from "src/repositories/labels/createOrganizationLabelsRepository";
import { createOrganizationMetricsRepository } from "src/repositories/metrics/createOrganizationMetricsRepository";
import { createOrganizationProgramsRepository } from "src/repositories/programs/createOrganizationProgramsRepository";
import { createOrganizationQuestionnairesRepository } from "src/repositories/questionnaires/createOrganizationQuestionnairesRepository";
import { createOrganizationStatisticsRepository } from "src/repositories/statistics/createOrganizationStatisticsRepository";
import { createOrganizationWorkoutsRepository } from "src/repositories/workouts/createOrganizationWorkoutsRepository";

interface OrganizationSpaceRepositoryProvidersProps extends PropsWithChildren {
  organizationSlug: string;
}

export function OrganizationSpaceRepositoryProviders(
  props: OrganizationSpaceRepositoryProvidersProps,
) {
  const organizations = useSuspenseQuery(organizationsQueryOptions()).data;
  const organization = organizations.find(
    (organization) => organization.slug === props.organizationSlug,
  );
  assert(organization, "Active organization is missing");

  const repositories = useMemo<SpaceRepositories>(() => {
    return {
      athletes: createOrganizationAthletesRepository(organization.id),
      metrics: createOrganizationMetricsRepository(organization.id),
      statistics: createOrganizationStatisticsRepository(organization.id),
      questionnaires: createOrganizationQuestionnairesRepository(
        organization.id,
      ),
      exercises: createOrganizationExercisesRepository(organization.id),
      labels: createOrganizationLabelsRepository(organization.id),
      programs: createOrganizationProgramsRepository(organization.id),
      workouts: createOrganizationWorkoutsRepository(organization.id),
    };
  }, [organization.id]);

  return (
    <SpaceRepositoryProviders repositories={repositories}>
      {props.children}
    </SpaceRepositoryProviders>
  );
}
